/**
 * Bundle Builder v3 - Refactored for Collections
 * Supports dynamic product slots (3-6 items)
 * Variant-based subscription logic
 */

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', class BundleBuilder extends HTMLElement {
    constructor() {
      super();

      // Element references
      this.tabs = this.querySelectorAll('[data-bundle-tab]');
      this.collectionGrids = this.querySelectorAll('[data-bundle-collection]');
      this.bundleItemsContainer = this.querySelector('[data-bundle-builder-items]');
      this.formEl = this.querySelector('[data-bundle-builder-form]');
      this.toggleFormEl = this.querySelector('[data-bundle-builder-form-toggle]');
      this.drawerCloseBtn = this.querySelector('[data-bundle-drawer-close]');
      this.overlayEl = this.querySelector('[data-bundle-overlay]');
      this.subscriptionToggle = this.querySelector('[data-subscription-toggle]');
      this.addToCartBtn = this.querySelector('[data-bundle-add-to-cart]');
      this.addToCartBtnsMobile = this.querySelectorAll('[data-bundle-add-to-cart-mobile]');
      this.itemCountEl = this.querySelector('[data-bundle-builder-item-count-current]');
      this.itemCountMobileEl = this.querySelector('[data-bundle-builder-item-count-mobile]');

      // Progress & Pricing (use querySelectorAll for elements that appear multiple times)
      this.progressFills = this.querySelectorAll('[data-bundle-progress-fill]');
      this.unlockTexts = this.querySelectorAll('[data-bundle-unlock-text], [data-bundle-unlock-text-mobile]');
      this.totalPriceEls = this.querySelectorAll('[data-bundle-total-price], [data-bundle-total-price-mobile]');
      this.subtotalEl = this.querySelector('[data-bundle-subtotal]');
      this.savingsEl = this.querySelector('[data-bundle-savings]');
      this.totalEl = this.querySelector('[data-bundle-total]');
      this.ctaPriceEls = this.querySelectorAll('[data-bundle-cta-price], [data-bundle-cta-price-mobile]');
      this.ctaTextMobileEl = this.querySelector('[data-bundle-cta-text-mobile]');

      // Data attributes
      this.minItems = parseInt(this.dataset.bundleBuilderMinItems, 10) || 2;
      this.maxItems = parseInt(this.dataset.bundleBuilderMaxItems, 10) || 6;
      this.bundleName = this.dataset.bundleBuilderName;
      this.bundleSlug = this.dataset.bundleBuilderSlug;
      this.hideFirstMilestoneOnSubscription = this.dataset.hideFirstMilestoneOnSubscription === 'true';
      this.freeShippingWithTheSubscription = this.dataset.freeShippingWithTheSubscription === 'true';
      this.setOneTimePurchaseAfterAdd = this.dataset.setOneTimePurchaseAfterAdd === 'true';
      this.addToCartBehavior = this.dataset.addToCartBehavior;
      this.useNewMilestones = this.dataset.useNewMilestones === 'true';

      // State
      this.bundleItems = [];
      this.currentSlots = this.minItems;
      this.storageKey = 'bundle-builder-v3-state';

      // Price-based milestones (amounts in cents)
      // Old system: 5 milestones
      // New system: Free Shipping + 2 savings callouts ($150/$10 off, $250/$20 off)
      if (this.useNewMilestones) {
        this.milestones = [
          { threshold: 10000, discount: 0, label: 'Free Shipping' },      // $100
          { threshold: 15000, discount: 1000, label: '$10 OFF' },       // $150 - $10 off
          { threshold: 25000, discount: 2000, label: '$20 OFF' }        // $250 - $20 off
        ];
      } else {
        this.milestones = [
          { threshold: 10000, discount: 0, label: 'Free Shipping' },      // $100
          { threshold: 11000, discount: 500, label: '$5 OFF' },           // $110 - $5 off
          { threshold: 16000, discount: 2000, label: '$20 OFF' },         // $160 - $20 off
          { threshold: 20000, discount: 3000, label: '$30 OFF' },         // $200 - $30 off
          { threshold: 25000, discount: 4000, label: '$40 OFF' }          // $250 - $40 off
        ];
      }

      // FIX for stuck loading state on back-button navigation
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          this.setLoadingState(false);
          this._makeCartDrawerVisible();
        }
      });

      // Handle browser back/forward buttons
      window.addEventListener('popstate', () => {
        this.handleUrlCollectionState();
      });

      // Initialize
      this.init();
    }

    init() {
      if (!this.formEl) return;

      this.setupTabs();
      this.handleUrlCollectionState(); // Check URL for collection parameter and auto-select
      this.setupProducts();
      this.setupBundleSlots();
      this.restoreBundleState(); // Restore state BEFORE setting up subscription toggle
      this.setupSubscriptionToggle();
      this.setupSubscriptionBenefitsToggle();
      this.setupFormToggle();
      this.setupAddToCart();
      this.updateMilestoneLabels(); // Update milestone labels with currency
      this.positionMilestones(); // Position milestones absolutely
      this.updateAllProductButtons();
      this.updatePricing(); // Initialize pricing on load
      this.updateFreeShippingCheckmark(this.subscriptionToggle?.checked || false); // Initialize checkmark state
    }


    // Update all product buttons to show correct state
    updateAllProductButtons() {
      const productCards = this.querySelectorAll('[data-bundle-builder-product]');

      productCards.forEach(card => {
        try {
          const productJsonEl = card.querySelector('[data-bundle-builder-product-json]');
          if (!productJsonEl) return;

          const product = JSON.parse(productJsonEl.textContent);
          const quantity = this.bundleItems.filter(item => item.productId === product.id).length;

          this.updateProductButton(card, quantity);
        } catch (error) {
          console.error('Error updating product button:', error);
        }
      });
    }

    // Handle URL collection state - check for collection parameter and auto-select
    handleUrlCollectionState() {
      if (!this.tabs || this.tabs.length === 0) return;

      // Get collection from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const collectionId = urlParams.get('collection');

      if (collectionId) {
        // Find the tab with matching collection handle
        const matchingTab = Array.from(this.tabs).find(tab => {
          return tab.dataset.bundleTab === collectionId;
        });

        if (matchingTab) {
          // Select the matching collection (without updating URL since it's already in URL)
          this.selectCollection(matchingTab, false);
        }
      }
    }

    // Select a collection (used by both URL state and tab clicks)
    selectCollection(tab, updateUrl = true) {
      // Update active tab
      this.tabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      // Show corresponding collection grid
      const collectionHandle = tab.dataset.bundleTab;
      
      // Update URL if requested (skip for initial load from URL)
      if (updateUrl) {
        this.updateUrlWithCollection(collectionHandle);
      }
      
      this.collectionGrids.forEach(grid => {
        if (grid.dataset.bundleCollection === collectionHandle) {
          grid.classList.remove('hidden');

          // Update button states for products in this collection
          setTimeout(() => {
            const productsInGrid = grid.querySelectorAll('[data-bundle-builder-product]');
            productsInGrid.forEach(card => {
              try {
                const productJsonEl = card.querySelector('[data-bundle-builder-product-json]');
                if (!productJsonEl) return;

                const product = JSON.parse(productJsonEl.textContent);
                const quantity = this.bundleItems.filter(item => item.productId === product.id).length;

                this.updateProductButton(card, quantity);
              } catch (error) {
                console.error('Error updating product button:', error);
              }
            });
          }, 50);
        } else {
          grid.classList.add('hidden');
        }
      });
    }

    // Update URL with collection parameter
    updateUrlWithCollection(collectionHandle, replaceState = false) {
      const url = new URL(window.location);
      url.searchParams.set('collection', collectionHandle);
      // Use replaceState for initial load, pushState for user interactions (allows back button)
      if (replaceState) {
        window.history.replaceState({}, '', url);
      } else {
        window.history.pushState({}, '', url);
      }
    }

    // Setup collection tabs switching
    setupTabs() {
      if (!this.tabs || this.tabs.length === 0) return;

      this.tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          // Select the collection (will update URL automatically)
          this.selectCollection(tab);
        });
      });
    }

    // Setup form toggle for mobile
    setupFormToggle() {
      if (!this.toggleFormEl) return;

      this.toggleFormEl.addEventListener('click', (e) => {
        // Don't open drawer if clicking on submit button
        if (e.target.closest('[data-bundle-add-to-cart-mobile]')) {
          e.stopPropagation();
          return;
        }

        // Don't open if already open
        if (this.formEl.classList.contains('active')) {
          return;
        }

        this.openDrawer();
      });

      // Handle drawer close button
      if (this.drawerCloseBtn) {
        this.drawerCloseBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.closeDrawer();
        });
      }

      // Handle mobile add to cart
      if (this.addToCartBtnsMobile) {
        this.addToCartBtnsMobile.forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.addBundleToCart();
          });
        });
      }

      // Click on overlay to close drawer
      if (this.overlayEl) {
        this.overlayEl.addEventListener('click', () => {
          this.closeDrawer();
        });
      }
    }

    // Open mobile drawer
    openDrawer() {
      if (!this.formEl) {
        console.error('Form element not found');
        return;
      }
      this.formEl.classList.add('active');
      this.toggleFormEl?.setAttribute('aria-expanded', 'true');

      // Show overlay
      if (this.overlayEl) {
        this.overlayEl.classList.add('active');
      }

      document.body.classList.add('bundle-drawer-open');
    }

    // Close mobile drawer
    closeDrawer() {
      if (!this.formEl) return;
      this.formEl.classList.remove('active');
      this.toggleFormEl?.setAttribute('aria-expanded', 'false');

      // Hide overlay
      if (this.overlayEl) {
        this.overlayEl.classList.remove('active');
      }

      document.body.classList.remove('bundle-drawer-open');
    }

    // Setup product add buttons
    setupProducts() {
      // Delegate event handling for dynamically loaded products
      this.addEventListener('click', (e) => {
        // Handle add button
        const addBtn = e.target.closest('[data-bundle-builder-add]');
        if (addBtn && !addBtn.classList.contains('bundle-builder__add-btn--with-quantity')) {
          e.preventDefault();
          const productCard = addBtn.closest('[data-bundle-builder-product]');
          if (productCard) {
            this.addProductToBundle(productCard);
          }
          return;
        }

        // Handle quantity increment
        const qtyIncBtn = e.target.closest('[data-bundle-qty-inc]');
        if (qtyIncBtn) {
          e.preventDefault();
          const productCard = qtyIncBtn.closest('[data-bundle-builder-product]');
          if (productCard) {
            this.incrementProductQuantity(productCard);
          }
          return;
        }

        // Handle quantity decrement
        const qtyDecBtn = e.target.closest('[data-bundle-qty-dec]');
        if (qtyDecBtn) {
          e.preventDefault();
          const productCard = qtyDecBtn.closest('[data-bundle-builder-product]');
          if (productCard) {
            this.decrementProductQuantity(productCard);
          }
          return;
        }
      });
    }

    // Setup bundle item slots with dynamic add/remove
    setupBundleSlots() {
      // Delegate remove button handling
      this.bundleItemsContainer.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-bundle-builder-item-remove]');
        if (!removeBtn) return;

        e.preventDefault();
        const slotEl = removeBtn.closest('[data-bundle-builder-item]');
        const slotIndex = parseInt(slotEl.dataset.bundleBuilderItem, 10) - 1;
        this.removeProductFromSlot(slotIndex);
      });
    }

    // Setup subscription toggle
    setupSubscriptionToggle() {
      if (!this.subscriptionToggle) return;

      // Get label element, benefits section, and frequency dropdown
      const labelEl = this.querySelector('[data-purchase-type-label]');
      const benefitsSection = this.querySelector('[data-subscription-benefits]');

      // Update label, benefits, and frequency visibility based on initial state
      this.updateSubscriptionUI();

      this.subscriptionToggle.addEventListener('change', () => {
        // Update all items in bundle to match subscription state
        const isSubscription = this.subscriptionToggle.checked;

        this.bundleItems = this.bundleItems.map(item => {
          const variant = this.getVariantForPurchaseType(item.product, isSubscription);
          return {
            ...item,
            variant,
            isSubscription
          };
        });

        // Update UI
        this.updateSubscriptionUI();
        this.updatePricing();
        this.renderBundleSlots();
        this.saveBundleState();
        this.updateMilestoneLabels();
      });
    }

    // Update subscription UI (label, benefits, frequency dropdown)
    updateSubscriptionUI() {
      const subscribeLabel = this.querySelector('[data-purchase-type-label-subscribe]');
      const otpLabel = this.querySelector('[data-purchase-type-label-otp]');
      const benefitsSection = this.querySelector('[data-subscription-benefits]');
      const benefitsList = this.querySelector('.bundle-builder__benefits-list');
      const isSubscription = this.subscriptionToggle?.checked || false;

      // Calculate dynamic save text based on product count
      let saveText = 'Save 10%';

      // Update label text
      if (subscribeLabel && otpLabel) {
        if (isSubscription) {
          subscribeLabel.classList.remove('hidden');
          otpLabel.classList.add('hidden');
        } else {
          subscribeLabel.classList.add('hidden');
          otpLabel.classList.remove('hidden');
        }
      }

      // Always keep benefits section visible, but control its state
      if (benefitsSection) {
        benefitsSection.classList.remove('hidden');

        if (isSubscription) {
          benefitsSection.classList.remove('collapsed');
          benefitsSection.dataset.state = 'subscription';
        } else {
          benefitsSection.classList.add('collapsed');
          benefitsSection.dataset.state = 'otp';
        }
      }

      // Show/hide benefits list based on state
      if (benefitsList) {
        if (isSubscription) {
          benefitsList.style.display = '';
        } else {
          benefitsList.style.display = 'none';
        }
      }

      // Update free shipping checkmark icon
      this.updateFreeShippingCheckmark(isSubscription);
    }

    // Update free shipping checkmark icon visibility
    updateFreeShippingCheckmark(isSubscription) {
      const shouldShowCheckmark = (this.hideFirstMilestoneOnSubscription || this.freeShippingWithTheSubscription) && isSubscription;
      const freeShippingMilestones = this.querySelectorAll('[data-free-shipping-milestone]');
      
      freeShippingMilestones.forEach(milestone => {
        const amountBadge = milestone.querySelector('[data-milestone-amount]');
        const amountText = milestone.querySelector('.bundle-builder__milestone-amount-text');
        const checkmark = milestone.querySelector('.bundle-builder__milestone-checkmark');
        
        if (amountBadge && amountText && checkmark) {
          if (shouldShowCheckmark) {
            // Hide amount text, show checkmark
            amountText.style.display = 'none';
            checkmark.style.display = 'inline-block';
          } else {
            // Show amount text, hide checkmark
            amountText.style.display = '';
            checkmark.style.display = 'none';
          }
        }
      });
    }

    // Setup subscription benefits collapsible toggle
    setupSubscriptionBenefitsToggle() {
      const benefitsSection = this.querySelector('[data-subscription-benefits]');
      const benefitsHeader = this.querySelector('[data-subscription-benefits-header]');

      if (!benefitsSection || !benefitsHeader) return;

      // Set initial state based on subscription toggle
      const isSubscription = this.subscriptionToggle?.checked || false;
      
      if (!isSubscription) {
        benefitsSection.classList.add('collapsed');
        benefitsSection.dataset.state = 'otp';
      } else {
        benefitsSection.classList.remove('collapsed');
        benefitsSection.dataset.state = 'subscription';
      }
    }

    // Setup add to cart button
    setupAddToCart() {
      if (!this.addToCartBtn) return;

      this.addToCartBtn.addEventListener('click', () => {
        this.addBundleToCart();
      });
    }

    // Add product to bundle
    addProductToBundle(productCard) {
      const productJsonEl = productCard.querySelector('[data-bundle-builder-product-json]');
      if (!productJsonEl) return;

      try {
        const product = JSON.parse(productJsonEl.textContent);

        // Check if we've reached max items
        if (this.bundleItems.length >= this.maxItems) {
          alert(`Maximum ${this.maxItems} products allowed in bundle`);
          return;
        }

        // Get appropriate variant based on subscription toggle
        const isSubscription = this.subscriptionToggle?.checked || false;
        const variant = this.getVariantForPurchaseType(product, isSubscription);
        if (!variant) {
          console.error('No suitable variant found for product:', product.title);
          return;
        }

        // Get collection ID from product card
        const collectionId = productCard.dataset.bundleCollectionId;

        // Add to bundle items array
        this.bundleItems.push({
          product,
          variant,
          isSubscription,
          productId: product.id,
          collectionId: collectionId
        });

        // Update button to show quantity controls
        this.updateProductButton(productCard, 1);

        // Update UI
        this.renderBundleSlots();
        this.updatePricing();
        this.checkIfNeedMoreSlots();
        this.saveBundleState();

        // Scroll to sidebar on mobile
        if (window.innerWidth < 768) {
          this.formEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      } catch (error) {
        console.error('Error adding product to bundle:', error);
      }
    }

    // Increment product quantity
    incrementProductQuantity(productCard) {
      const productJsonEl = productCard.querySelector('[data-bundle-builder-product-json]');
      if (!productJsonEl) return;

      try {
        const product = JSON.parse(productJsonEl.textContent);
        const currentQty = this.bundleItems.filter(item => item.productId === product.id).length;

        // Check if we've reached max items
        if (this.bundleItems.length >= this.maxItems) {
          alert(`Maximum ${this.maxItems} products allowed in bundle`);
          return;
        }

        // Get appropriate variant based on subscription toggle
        const isSubscription = this.subscriptionToggle?.checked || false;
        const variant = this.getVariantForPurchaseType(product, isSubscription);

        if (!variant) return;

        // Get collection ID from product card
        const collectionId = productCard.dataset.bundleCollectionId;

        // Add another instance
        this.bundleItems.push({
          product,
          variant,
          isSubscription,
          productId: product.id,
          collectionId: collectionId
        });

        // Update button
        this.updateProductButton(productCard, currentQty + 1);

        // Update UI
        this.renderBundleSlots();
        this.updatePricing();
        this.checkIfNeedMoreSlots();
        this.saveBundleState();
      } catch (error) {
        console.error('Error incrementing product quantity:', error);
      }
    }

    // Decrement product quantity
    decrementProductQuantity(productCard) {
      const productJsonEl = productCard.querySelector('[data-bundle-builder-product-json]');
      if (!productJsonEl) return;

      try {
        const product = JSON.parse(productJsonEl.textContent);
        const productIndex = this.bundleItems.findIndex(item => item.productId === product.id);

        if (productIndex === -1) return;

        // Remove one instance
        this.bundleItems.splice(productIndex, 1);

        // Get new quantity
        const newQty = this.bundleItems.filter(item => item.productId === product.id).length;

        // Update button
        this.updateProductButton(productCard, newQty);

        // Update UI
        this.renderBundleSlots();
        this.updatePricing();
        this.saveBundleState();
      } catch (error) {
        console.error('Error decrementing product quantity:', error);
      }
    }

    // Update product button to show quantity or add state
    updateProductButton(productCard, quantity) {
      const addBtn = productCard.querySelector('[data-bundle-builder-add]');
      if (!addBtn) return;

      if (quantity > 0) {
        // Show quantity controls
        addBtn.classList.add('bundle-builder__add-btn--with-quantity');
        addBtn.innerHTML = `
          <button type="button" class="bundle-builder__qty-btn" data-bundle-qty-dec aria-label="Decrease quantity">
            <svg width="9" height="2" viewBox="0 0 9 2" fill="none">
              <rect width="9" height="2" rx="1" fill="currentColor"/>
            </svg>
          </button>
          <span class="bundle-builder__qty-number">${quantity}</span>
          <button type="button" class="bundle-builder__qty-btn" data-bundle-qty-inc aria-label="Increase quantity">
            <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
              <path d="M4.5 0.5V8.5M0.5 4.5H8.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        `;
      } else {
        // Show add button
        addBtn.classList.remove('bundle-builder__add-btn--with-quantity');
        addBtn.textContent = 'ADD +';
      }
    }

    // Get variant based on purchase type (one-time or subscription)
    // Returns variant with selling plan data if subscription
    getVariantForPurchaseType(product, isSubscription) {
      if (!product.variants || product.variants.length === 0) return null;

      if (isSubscription) {
        // Check if product has selling plans
        const hasSellingPlans = product.requires_selling_plan || 
          (product.selling_plan_groups && product.selling_plan_groups.length > 0);

        // Find variant with selling plan allocations that includes "1 month"
        const subVariantWithPlan = product.variants.find(v => {
          if (!v.available || !v.selling_plan_allocations || v.selling_plan_allocations.length === 0) {
            return false;
          }

          // Check if variant has a "1 month" selling plan
          const hasMonthlyPlan = v.selling_plan_allocations.some(allocation => {
            const planName = allocation.selling_plan_group_name || '';
            return planName.toLowerCase().includes('1 month') || 
                   planName.toLowerCase().includes('monthly') ||
                   planName.toLowerCase().includes('month');
          });

          // Also check variant title
          const titleMatches = v.title.toLowerCase().includes('1 month') ||
            v.title.toLowerCase().includes('month') ||
            v.title.toLowerCase().includes('subscription');

          return hasMonthlyPlan || titleMatches;
        });

        if (subVariantWithPlan) {
          return subVariantWithPlan;
        }

        // Fallback: try to find subscription variant by title only
        const subVariant = product.variants.find(v =>
          v.available && (
            v.title.toLowerCase().includes('1 month') ||
            v.title.toLowerCase().includes('month') ||
            v.title.toLowerCase().includes('subscription')
          )
        );

        if (subVariant) {
          return subVariant;
        }
      }

      // Look for "one-time purchase" variant or default variant
      const oneTimeVariant = product.variants.find(v =>
        v.available && (
          v.title.toLowerCase().includes('one-time') ||
          v.title.toLowerCase().includes('one time') ||
          v.title.toLowerCase().includes('otp')
        )
      );

      if (oneTimeVariant) {
        return oneTimeVariant;
      }

      // Fallback to first available variant
      return product.variants.find(v => v.available) || product.variants[0];
    }

    // Get selling plan ID for subscription variant (prioritize 1 month)
    getSellingPlanId(variant) {
      if (!variant || !variant.selling_plan_allocations || variant.selling_plan_allocations.length === 0) {
        return null;
      }

      // Try to find "1 month" selling plan specifically
      const monthlyPlan = variant.selling_plan_allocations.find(allocation => {
        const planName = allocation.selling_plan_group_name || '';
        return planName.toLowerCase().includes('1 month') || 
               planName.toLowerCase().includes('monthly');
      });

      if (monthlyPlan) {
        return monthlyPlan.selling_plan_id;
      }

      // Fallback to first selling plan ID
      return variant.selling_plan_allocations[0].selling_plan_id;
    }

    // Remove product from specific slot
    removeProductFromSlot(index) {
      if (index < 0 || index >= this.bundleItems.length) return;

      const removedItem = this.bundleItems[index];
      this.bundleItems.splice(index, 1);

      // Update the product button for this product
      if (removedItem && removedItem.productId) {
        const productCard = this.querySelector(`[data-bundle-builder-product="${removedItem.productId}"]`);
        if (productCard) {
          const newQty = this.bundleItems.filter(item => item.productId === removedItem.productId).length;
          this.updateProductButton(productCard, newQty);
        }
      }

      // Check if we need to remove extra slots
      this.removeExtraSlots();

      this.renderBundleSlots();
      this.updatePricing();
      this.saveBundleState();
    }

    // Remove extra slots when products are deleted
    removeExtraSlots() {
      const filledSlots = this.bundleItems.length;

      // If we have more slots than needed and we're above minimum
      if (this.currentSlots > this.minItems && filledSlots < this.currentSlots - 1) {
        // Calculate how many slots we should have
        const neededSlots = Math.max(filledSlots + 1, this.minItems);

        // Remove excess slot elements
        while (this.currentSlots > neededSlots) {
          const lastSlot = this.bundleItemsContainer.querySelector(`[data-bundle-builder-item="${this.currentSlots}"]`);
          if (lastSlot) {
            lastSlot.remove();
          }
          this.currentSlots--;
        }
      }
    }

    // Check if we need to add more slots (up to max)
    checkIfNeedMoreSlots() {
      const filledSlots = this.bundleItems.length;

      // If all current slots are filled and we haven't reached max
      if (filledSlots === this.currentSlots && this.currentSlots < this.maxItems) {
        this.currentSlots++;
        this.addNewSlot();
      }
    }

    // Add a new empty slot
    addNewSlot() {
      const slotHTML = `
        <div 
          class="bundle-builder__item bundle-builder__item--empty" 
          data-bundle-builder-item="${this.currentSlots}"
        >
          <div class="bundle-builder__item-placeholder">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 4V16M4 10H16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </div>
          <div class="bundle-builder__item-content" data-bundle-item-content style="display: none;">
            <div class="bundle-builder__item-image" data-bundle-item-image></div>
            <div class="bundle-builder__item-details">
              <p class="bundle-builder__item-title" data-bundle-item-title></p>
              <p class="bundle-builder__item-price" data-bundle-item-price></p>
            </div>
            <button 
              type="button" 
              class="bundle-builder__item-remove" 
              data-bundle-builder-item-remove
              aria-label="Remove item"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2"/>
              </svg>
            </button>
          </div>
          <p class="bundle-builder__item-add-text" data-bundle-item-add-text>
            Select Product
          </p>
        </div>
      `;

      this.bundleItemsContainer.insertAdjacentHTML('beforeend', slotHTML);
    }

    // Render all bundle slots
    renderBundleSlots() {
      const slots = this.bundleItemsContainer.querySelectorAll('[data-bundle-builder-item]');
      const productCount = this.bundleItems.length;

      // Calculate how many slots should be visible
      let visibleSlots = this.minItems; // Default to 3

      if (productCount < 3) {
        visibleSlots = 3; // Always show at least 3
      } else if (productCount === 3) {
        visibleSlots = 4;
      } else if (productCount === 4) {
        visibleSlots = 5;
      } else if (productCount >= 5) {
        visibleSlots = 6;
      }

      slots.forEach((slot, index) => {
        const item = this.bundleItems[index];
        const slotNumber = index + 1;
        const placeholder = slot.querySelector('.bundle-builder__item-placeholder');
        const content = slot.querySelector('[data-bundle-item-content]');
        const addText = slot.querySelector('[data-bundle-item-add-text]');

        // Hide slots beyond the visible count
        if (slotNumber > visibleSlots) {
          slot.style.display = 'none';
          return;
        } else {
          slot.style.display = '';
        }

        if (item) {
          // Filled slot
          slot.classList.remove('bundle-builder__item--empty');
          slot.classList.add('bundle-builder__item--filled');

          if (placeholder) placeholder.style.display = 'none';
          if (addText) addText.style.display = 'none';
          if (content) {
            content.style.display = 'flex';

            // Update content
            const imageEl = content.querySelector('[data-bundle-item-image]');
            const titleEl = content.querySelector('[data-bundle-item-title]');
            const priceEl = content.querySelector('[data-bundle-item-price]');

            if (imageEl && item.product.featured_image) {
              imageEl.innerHTML = `<img src="${item.product.featured_image}" alt="${item.product.title}" loading="lazy">`;
            }

            if (titleEl) {
              titleEl.textContent = item.product.title ;
            }

            if (priceEl) {
              const price = this.getItemPrice(item);
              priceEl.textContent = this.formatMoney(price, false, true); // Skip conversion - already in customer currency
            }
          }
        } else {
          // Empty slot
          slot.classList.add('bundle-builder__item--empty');
          slot.classList.remove('bundle-builder__item--filled');

          if (placeholder) placeholder.style.display = '';
          if (addText) {
            addText.style.display = '';
            
            // Show next milestone message based on current subtotal
            const currentSubtotal = this.calculateSubtotal();
            const nextMilestone = this.getNextMilestone(currentSubtotal);
            
            if (nextMilestone) {
              // Convert threshold to customer currency, then calculate amount needed
              const convertedThreshold = this.convertThreshold(nextMilestone.threshold);
              const amountNeeded = convertedThreshold - currentSubtotal;
              // Skip conversion since we already converted threshold
              addText.textContent = `${this.formatMoney(amountNeeded, false, true)} to unlock ${nextMilestone.label}`;
            } else {
              addText.textContent = 'Select Product';
            }
          }
          if (content) content.style.display = 'none';
        }
      });

      // Update item count
      if (this.itemCountEl) {
        this.itemCountEl.textContent = this.bundleItems.length;
      }

      // Update mobile item count
      if (this.itemCountMobileEl) {
        this.itemCountMobileEl.textContent = this.bundleItems.length;
      }
    }

    // Get milestone for specific price (in cents)
    getMilestoneForPrice(price) {
      // Find the next milestone that would be unlocked
      for (let i = 0; i < this.milestones.length; i++) {
        if (price < this.milestones[i].threshold) {
          return this.milestones[i];
        }
      }
      return null; // All milestones unlocked
    }

    // Get item price (considering subscription discount if applicable)
    getItemPrice(item) {
      if (!item.isSubscription) {
        return item.variant.price;
      }

      // Check for selling plan allocation
      if (item.variant.selling_plan_allocations && item.variant.selling_plan_allocations.length > 0) {
        // Use first selling plan price
        const allocation = item.variant.selling_plan_allocations[0];
        return allocation.price;
      }

      // Fallback: apply 15% discount
      return Math.round(item.variant.price * 0.85);
    }

    // Calculate subtotal (prices already in customer currency)
    calculateSubtotal() {
      const total = this.bundleItems.reduce((sum, item) => {
        const itemPrice = this.getItemPrice(item);
        return sum + itemPrice;
      }, 0);
      
      return total;
    }

    // Calculate savings
    calculateSavings() {
      const originalTotal = this.bundleItems.reduce((sum, item) => {
        return sum + item.variant.price;
      }, 0);

      const subtotal = this.calculateSubtotal();
      const milestone = this.getCurrentMilestone(subtotal);

      // Calculate milestone discount (fixed dollar amounts in USD, convert to customer currency)
      let milestoneDiscount = 0;
      if (milestone && milestone.discount > 0) {
        milestoneDiscount = this.convertThreshold(milestone.discount);
      }

      return (originalTotal - subtotal) + milestoneDiscount;
    }

    // Get current milestone based on price (in customer currency cents)
    // Note: product prices are in customer currency, so we convert thresholds for comparison
    getCurrentMilestone(price) {
      let current = null;
      for (const milestone of this.milestones) {
        const convertedThreshold = this.convertThreshold(milestone.threshold);
        if (price >= convertedThreshold) {
          current = milestone;
        } else {
          break;
        }
      }
      return current;
    }

    // Get next milestone to unlock based on price (in customer currency cents)
    // Note: product prices are in customer currency, so we convert thresholds for comparison
    getNextMilestone(price) {
      for (const [ind, milestone] of this.milestones.entries()) {
        if (this.hideFirstMilestoneOnSubscription && ind === 0 && this.subscriptionToggle.checked) {
          continue
        }
        const convertedThreshold = this.convertThreshold(milestone.threshold);
        if (price < convertedThreshold) {
          return milestone;
        }
      }
      return null;
    }

    // Calculate progress bar percentage mapped to visual milestone positions
    // Progress stops just before reaching the next milestone indicator (like Rebuy)
    // Subtotal is in customer currency, so we convert thresholds for comparison
    calculateProgressPercentage(subtotal) {
      const milestoneCount = this.milestones.length;
      
      // Milestone indicator width compensation (in percentage)
      // This creates space so the bar doesn't overlap with the milestone badge
      const indicatorOffset = 2; // ~2% space before each milestone
      
      // When subscription is active and free shipping is included, we've already passed the first milestone
      const shouldStartAtFirstMilestone = (this.hideFirstMilestoneOnSubscription || this.freeShippingWithTheSubscription) && this.subscriptionToggle.checked;
      
      // If subscription is active and free shipping is included, treat subtotal as if it's at least at first milestone
      let effectiveSubtotal = subtotal;
      if (shouldStartAtFirstMilestone && this.milestones.length > 0) {
        const firstMilestoneThreshold = this.convertThreshold(this.milestones[0].threshold);
        effectiveSubtotal = Math.max(subtotal, firstMilestoneThreshold);
      }
      
      // Find which milestone we've actually completed
      let currentMilestoneIndex = -1;
      
      for (let i = 0; i < this.milestones.length; i++) {
        const convertedThreshold = this.convertThreshold(this.milestones[i].threshold);
        if (effectiveSubtotal >= convertedThreshold) {
          currentMilestoneIndex = i;
        } else {
          break;
        }
      }
      
      // If subscription is active and free shipping is included, ensure we start from first milestone (index 0)
      if (shouldStartAtFirstMilestone && currentMilestoneIndex === -1) {
        currentMilestoneIndex = 0; // We've passed the first milestone (Free Shipping)
      }
      
      // If we've passed all milestones, return 100%
      if (currentMilestoneIndex === this.milestones.length - 1) {
        return 100;
      }
      
      // Get current and next milestone data
      const nextMilestone = this.milestones[currentMilestoneIndex + 1];
      
      // Calculate visual positions (evenly distributed)
      const currentVisualPosition = currentMilestoneIndex >= 0 
        ? ((currentMilestoneIndex + 1) / milestoneCount) * 100 
        : 0;
      const nextVisualPosition = ((currentMilestoneIndex + 2) / milestoneCount) * 100;
      
      // Get actual threshold values (converted to customer currency)
      const startThreshold = currentMilestoneIndex >= 0 
        ? this.convertThreshold(this.milestones[currentMilestoneIndex].threshold)
        : 0;
      const endThreshold = this.convertThreshold(nextMilestone.threshold);
      
      // Calculate progress within the current segment (based on actual dollars)
      const segmentRange = endThreshold - startThreshold;
      const progressInSegment = effectiveSubtotal - startThreshold;
      const segmentProgress = segmentRange > 0 ? (progressInSegment / segmentRange) : 0; // 0 to 1
      
      // Map to visual segment (between visual positions)
      const visualSegmentWidth = nextVisualPosition - currentVisualPosition;
      const visualProgress = currentVisualPosition + (segmentProgress * visualSegmentWidth);
      
      // Cap progress to stop before the next milestone indicator
      const maxAllowedProgress = nextVisualPosition - indicatorOffset;
      const finalProgress = Math.min(visualProgress, maxAllowedProgress);
      
      return Math.max(0, Math.min(finalProgress, 100));
    }

    // Update all pricing displays
    updatePricing() {
      const subtotal = this.calculateSubtotal();
      const savings = this.calculateSavings();
      const productCount = this.bundleItems.length;
      const milestone = this.getCurrentMilestone(subtotal);
      const nextMilestone = this.getNextMilestone(subtotal);

      // Calculate milestone discount (fixed dollar amounts in USD, convert to customer currency)
      let milestoneDiscount = 0;
      if (milestone && milestone.discount > 0) {
        milestoneDiscount = this.convertThreshold(milestone.discount);
      }

      const total = subtotal - milestoneDiscount;

      // Update price displays (skip conversion - prices already in customer currency)
      if (this.subtotalEl) {
        this.subtotalEl.textContent = this.formatMoney(subtotal, false, true);
      }

      if (this.savingsEl) {
        this.savingsEl.textContent = this.formatMoney(savings, false, true);
        this.savingsEl.closest('.bundle-builder__price-row').style.display = savings > 0 ? '' : 'none';
      }

      if (this.totalEl) {
        this.totalEl.textContent = this.formatMoney(total, false, true);
      }

      // Update all CTA price elements (mobile + desktop)
      this.ctaPriceEls.forEach(el => {
        el.textContent = this.formatMoney(total, false, true);
      });

      // Update shipping display
      this.updateShippingDisplay(subtotal);

      // Update all progress bars (mobile + desktop) - segment-based calculation
      const progress = this.calculateProgressPercentage(subtotal);
      
      // Calculate first milestone position based on number of milestones
      const milestoneCount = this.milestones.length;
      const firstMilestonePosition = (1 / milestoneCount) * 100; // e.g., 33.33% for 3 milestones, 20% for 5 milestones
      
      // When subscription is active and free shipping is included, start progress at first milestone
      const shouldStartAtFirstMilestone = (this.hideFirstMilestoneOnSubscription || this.freeShippingWithTheSubscription) && this.subscriptionToggle.checked;
      
      this.progressFills.forEach(el => {
        let finalProgress = progress;
        
        if (shouldStartAtFirstMilestone) {
          // When subscription is active, free shipping is already unlocked
          // So progress should start at first milestone position (33% for new system, 20% for old)
          // If calculated progress is less than first milestone, use first milestone position
          finalProgress = Math.max(firstMilestonePosition, progress);
          el.style.minWidth = `${firstMilestonePosition}%`;
        } else {
          el.style.minWidth = '0%';
        }
        
        el.style.width = `${Math.min(finalProgress, 100)}%`;
      });

      // Update all unlock text elements (mobile + desktop)
      const unlockTextContent = nextMilestone 
        ? (() => {
            // Convert threshold to customer currency, then calculate amount needed
            const convertedThreshold = this.convertThreshold(nextMilestone.threshold);
            const amountNeeded = convertedThreshold - subtotal;
            // Skip conversion in formatMoney since we already converted
            return `Spend ${this.formatMoney(amountNeeded, false, true)} more to unlock ${nextMilestone.label}.`;
          })()
        : 'You\'ve unlocked all discounts!';
      
      this.unlockTexts.forEach(el => {
        el.textContent = unlockTextContent;
      });

      // Update milestones visual state
      this.updateMilestonesUI(subtotal);

      // Update subscription UI with dynamic save badge
      this.updateSubscriptionUI();

      // Enable/disable add to cart
      this.updateAddToCartButton();

      // Re-render slots to update "add more" text
      this.renderBundleSlots();
    }

    // Update shipping display based on subtotal price (customer currency)
    updateShippingDisplay(subtotal) {
      const shippingEl = this.querySelector('[data-bundle-shipping]');
      if (!shippingEl) return;

      // Shipping cost in cents (USD base), convert to customer currency
      const shippingCost = 699;
      const formattedShipping = this.formatMoney(shippingCost); // Will convert to customer currency

      // Free shipping unlocked at $100 USD (10000 cents), convert for comparison of if 
      let freeShippingThreshold = 0
      if (this.freeShippingWithTheSubscription && this.subscriptionToggle.checked) {
        freeShippingThreshold = 0
      } else {
        freeShippingThreshold = this.convertThreshold(10000);
      }
      if (subtotal >= freeShippingThreshold) {
        shippingEl.innerHTML = `<span class="bundle-builder__strike">${formattedShipping}</span> FREE`;
      } else {
        shippingEl.textContent = formattedShipping;
      }
    }

    // Update milestones UI based on subtotal price (customer currency)
    updateMilestonesUI(subtotal) {
      // Update all milestone elements (mobile + desktop)
      const milestoneEls = this.querySelectorAll('[data-milestone-step]');
      
      // Check if subscription is active and free shipping is included
      const isSubscription = this.subscriptionToggle?.checked || false;
      const shouldShowFreeShippingAsActive = (this.hideFirstMilestoneOnSubscription || this.freeShippingWithTheSubscription) && isSubscription;
      
      milestoneEls.forEach((el) => {
        // Get milestone step from data attribute (1-5)
        const stepNumber = parseInt(el.dataset.milestoneStep, 10);
        const milestone = this.milestones[stepNumber - 1]; // Array is 0-indexed
        
        if (!milestone) return; // Skip if no milestone data for this step
        
        // Check if this is the free shipping milestone (first milestone, step 1)
        const isFreeShippingMilestone = el.hasAttribute('data-free-shipping-milestone') || stepNumber === 1;
        
        // Convert threshold to customer currency for comparison
        const convertedThreshold = this.convertThreshold(milestone.threshold);
        let shouldBeActive = subtotal >= convertedThreshold;
        
        // If this is the free shipping milestone and subscription is active, mark it as active
        if (isFreeShippingMilestone && shouldShowFreeShippingAsActive) {
          shouldBeActive = true;
        }
        
        if (shouldBeActive) {
          el.classList.add('active');
        } else {
          el.classList.remove('active');
        }
      });
    }

    // Update milestone labels with currency conversion
    updateMilestoneLabels() {
      const milestoneEls = this.querySelectorAll('[data-milestone-step]');
      milestoneEls.forEach((el, ind) => {
        // Get milestone step from data attribute (1-5)
        const stepNumber = parseInt(el.dataset.milestoneStep, 10);
        const milestone = this.milestones[stepNumber - 1]; // Array is 0-indexed
        
        if (!milestone) return;

        // Get the price label (the badge element)
        const amountBadge = el.querySelector('[data-milestone-amount]');
        const amountText = el.querySelector('.bundle-builder__milestone-amount-text');
        
        if (amountBadge && amountText) {
          // Check if this is the free shipping milestone and subscription is active
          const isFreeShippingMilestone = el.hasAttribute('data-free-shipping-milestone');
          const shouldHideAmount = this.hideFirstMilestoneOnSubscription && this.subscriptionToggle.checked && isFreeShippingMilestone;
          
          if (
            this.hideFirstMilestoneOnSubscription && this.subscriptionToggle.checked &&
            (ind === 0 || ind === Math.floor(milestoneEls.length / 2)) &&
            !isFreeShippingMilestone
          ) {
            amountBadge.classList.add("hidden");
            el.classList.add("top37px");
          } else {
            // formatMoney will convert to current currency and round, then remove decimals
            amountText.textContent = this.formatMoney(
              milestone.threshold,
              true
            );
            amountBadge.classList.remove("hidden");
            el.classList.remove("top37px");
          }
        }
      });
    }

    // Position milestones absolutely - evenly distributed for better visual spacing
    // Regardless of actual threshold values (prevents milestones bunching up)
    positionMilestones() {
      const milestoneEls = this.querySelectorAll('[data-milestone-step]');
      const milestoneCount = this.milestones.length;
      
      milestoneEls.forEach((el) => {
        const stepNumber = parseInt(el.dataset.milestoneStep, 10);
        const milestone = this.milestones[stepNumber - 1];
        
        if (!milestone) return;
        
        // Evenly distribute milestones across the progress bar
        // First milestone at 20%, second at 40%, etc.
        const position = (stepNumber / milestoneCount) * 100;
        
        // Store the position for progress bar calculations
        el.dataset.milestonePosition = position;
        
        // Set left position with transform: translateX(-50%) for centering
        el.style.left = `${position}%`;
      });
    }

    // Update add to cart button state
    updateAddToCartButton() {
      const isEnabled = this.bundleItems.length >= this.minItems;

      // Update desktop button
      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = !isEnabled;
        if (isEnabled) {
          this.addToCartBtn.classList.remove('disabled');
        } else {
          this.addToCartBtn.classList.add('disabled');
        }
      }

      // Update mobile button
      if (this.addToCartBtnsMobile) {
        this.addToCartBtnsMobile.forEach(btn => {
          btn.disabled = !isEnabled;
          if (isEnabled) {
            btn.classList.remove('disabled');
          } else {
            btn.classList.add('disabled');
          }
        });
      }
    }

    _makeCartDrawerVisible() {
      if (document.body.classList.contains('BYOB-page')) {
        document.body.classList.remove('BYOB-page');
      }
    }

    _makeCartDrawerHidden() {
      document.body.classList.add('BYOB-page');
    }

    // --- GWP (Gift With Purchase) Checkout Helpers ---
    _waitForGWPThenCheckout() {
      const targetClass = 'gwp-success-trigger';

      // Check immediately in case the class was already added
      if (document.body.classList.contains(targetClass)) {
        this._proceedToCheckout();
        return;
      }

      // Set up MutationObserver to watch for the class being added
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.attributeName === 'class' && document.body.classList.contains(targetClass)) {
            observer.disconnect();
            this._proceedToCheckout();
            return;
          }
        }
      });

      observer.observe(document.body, { attributes: true });

      setTimeout(()=>{
        this._proceedToCheckout();
      }, 3000)
    }

    _proceedToCheckout() {
      const checkoutButtonSelector = '.rebuy-cart__checkout-button';
      this._makeCartDrawerHidden();
        const checkoutButton = document.querySelector(checkoutButtonSelector);
        if (checkoutButton) {
          // The 'pageshow' event will handle resetting state if the user navigates back.
          checkoutButton.click(); // This will navigate to checkout
        } else {
          // FIX for stuck loading: reset state before fallback
          this.setLoadingState(false);
          this._makeCartDrawerVisible(); // Restore visibility before fallback
          window.location.href = '/checkout';
        }
    }
    // --- End of GWP Checkout Helpers ---

    // Add bundle to cart using Recharge Bundle API
    async addBundleToCart() {
      if (this.bundleItems.length < this.minItems) {
        alert(`Please add at least ${this.minItems} products to your bundle`);
        return;
      }

      const isSubscription = this.subscriptionToggle?.checked || false;

      // Get bundle product IDs from data attributes
      const bundleProductId = this.dataset.bundleProductId;
      const bundleVariantId = this.dataset.bundleVariantId;

      if (!bundleProductId || !bundleVariantId) {
        console.error('Missing bundle configuration. Add data-bundle-product-id and data-bundle-variant-id to the bundle-builder element.');
        alert('Bundle configuration error. Please refresh the page.');
        return;
      }

      // Set loading state
      this.setLoadingState(true);
      this._makeCartDrawerHidden();

      try {
        // Build Recharge bundle structure
        const bundleConfig = {
          externalProductId: bundleProductId,
          externalVariantId: bundleVariantId,
          selections: this.bundleItems.map(item => {
            const selection = {
              collectionId: item.collectionId, // Use collection from product card
              externalProductId: String(item.product.id),
              externalVariantId: String(item.variant.id),
              quantity: 1
            };

            // Add selling plan for subscriptions (Shopify Checkout)
            if (isSubscription) {
              const sellingPlanId = this.getSellingPlanId(item.variant);
              if (sellingPlanId) {
                selection.sellingPlan = String(sellingPlanId);
              }
            }

            return selection;
          })
        };

        // Validate bundle
        const isValid = await recharge.bundle.validateBundle(bundleConfig);
        if (isValid !== true) {
          console.error('Bundle validation failed:', isValid);
          alert('Bundle validation failed. Please check your selections.');
          this.setLoadingState(false);
          return;
        }

        // 1. Get dynamic bundle items for cart
        let cartItems = await recharge.bundle.getDynamicBundleItems(
          bundleConfig,
          this.bundleSlug || 'bundle'
        );

        // 2. Add a property (mutate an array)
        cartItems.forEach(item => {
          if (!item.properties) item.properties = {};
          item.properties['_bundle_key'] = 'byob';
        });

        // Add to cart
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: cartItems,
            attributes: {
              '_source_page': 'BYOB'
            }
          }),
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error('Failed to add bundle to cart');
        }

        await response.json();

        // Clear bundle state and reset UI
        this.clearBundleState();

        // CONDITIONAL LOGIC: Route to checkout or open cart based on theme setting
        if (this.addToCartBehavior === 'checkout') {
          // --- BEHAVIOR: GO TO CHECKOUT ---

          // FIX for flickering: Apply invisible class BEFORE opening the drawer
          this._makeCartDrawerHidden();

          // Open cart drawer to load its content, but keep it invisible
          if (typeof window.openCartDrawer === 'function') {
            window.openCartDrawer();
          }

          // Close mobile bundle drawer if open
          if (window.innerWidth < 768 && this.formEl.classList.contains('active')) {
            this.closeDrawer();
          }

          // Check if GWP is enabled and wait for gwp-added event before checkout
          if (window.GWP === true) {
            this._waitForGWPThenCheckout();
            console.log('this._waitForGWPThenCheckout();');
          } else {
            this._proceedToCheckout();
          }
        } else {
          // --- BEHAVIOR: GO TO CART ---
          // Open cart drawer if available
          if (typeof window.openCartDrawer === 'function') {
            window.openCartDrawer();
          }

          // Close mobile drawer if open
          if (window.innerWidth < 768 && this.formEl.classList.contains('active')) {
            this.closeDrawer();
          }

          // Remove loading state
          this.setLoadingState(false);
        }

      } catch (error) {
        console.error('Error adding bundle to cart:', error);
        alert('There was an error adding your bundle to cart. Please try again.');
        this.setLoadingState(false);
        this._makeCartDrawerVisible(); // Restore visibility on error
      }
    }

    // Set loading state on add to cart buttons
    setLoadingState(loading) {
      const buttons = [this.addToCartBtn, ...this.addToCartBtnsMobile].filter(Boolean);
      
      buttons.forEach(btn => {
        if (loading) {
          btn.classList.add('loading');
          btn.disabled = true;
        } else {
          btn.classList.remove('loading');
          btn.disabled = this.bundleItems.length < this.minItems;
        }
      });
    }

    // Format money helper with optional currency conversion
    // skipConversion = true for product prices (already in correct currency)
    // skipConversion = false for milestone thresholds (need USD -> current currency)
    formatMoney(cents, removeDecimals = false, skipConversion = false) {
      // Convert to current currency only if needed
      let convertedCents = cents;

      if (!skipConversion && window.Shopify?.currency?.active && window.Shopify?.currency?.rate) {
        const rate = parseFloat(window.Shopify.currency.rate);
        convertedCents = Math.round(cents * rate);
      }

      if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
        const format = window.theme?.moneyFormat || '${{amount}}';
        let formatted = window.Shopify.formatMoney(convertedCents, format);
        
        // Remove ALL decimals if requested (for milestone labels)
        if (removeDecimals) {
          // Remove .XX or ,XX at the end (handles different decimal separators)
          formatted = formatted.replace(/[.,]\d{2}$/, '');
        }
        
        return formatted;
      }

      // Fallback formatting
      const amount = (convertedCents / 100).toFixed(removeDecimals ? 0 : 2);
      return `$${amount}`;
    }

    // Convert amount to current currency (for text display)
    convertToCurrency(cents) {
      if (window.Shopify?.currency?.rate) {
        const rate = parseFloat(window.Shopify.currency.rate);
        return Math.round(cents * rate);
      }
      return cents;
    }

    // Convert milestone threshold to current currency
    // Only used for DISPLAY purposes (formatMoney handles product price conversion)
    convertThreshold(usdCents) {
      if (window.Shopify?.currency?.rate) {
        const rate = parseFloat(window.Shopify.currency.rate);
        return Math.round(usdCents * rate);
      }
      return usdCents;
    }

    // Generate unique ID
    generateGuid() {
      const d = new Date().getTime();
      const d2 = (window.performance && window.performance.now && (window.performance.now() * 1000)) || 0;

      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        let r = Math.random() * 16;

        if (d > 0) {
          r = (d + r) % 16 | 0;
        } else {
          r = (d2 + r) % 16 | 0;
        }

        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
      });
    }

    // Save bundle state to localStorage
    saveBundleState() {
      try {
        const state = {
          bundleItems: this.bundleItems.map(item => ({
            productId: item.product.id,
            variantId: item.variant.id,
            isSubscription: item.isSubscription,
            collectionId: item.collectionId
          })),
          currentSlots: this.currentSlots,
          isSubscription: this.subscriptionToggle?.checked || false,
          timestamp: Date.now()
        };

        localStorage.setItem(this.storageKey, JSON.stringify(state));
      } catch (error) {
        console.error('Error saving bundle state:', error);
      }
    }

    // Restore bundle state from localStorage
    restoreBundleState() {
      try {
        const savedState = localStorage.getItem(this.storageKey);
        if (!savedState) return;

        const state = JSON.parse(savedState);

        // Check if state is older than 24 hours
        if (Date.now() - state.timestamp > 86400000) {
          localStorage.removeItem(this.storageKey);
          return;
        }

        // Restore subscription toggle
        if (this.subscriptionToggle && typeof state.isSubscription === 'boolean') {
          this.subscriptionToggle.checked = state.isSubscription;
        }

        // Restore slots count
        if (state.currentSlots && state.currentSlots > this.minItems) {
          this.currentSlots = state.currentSlots;
          // Add extra slots
          for (let i = this.minItems; i < this.currentSlots; i++) {
            this.addNewSlot();
          }
        }

        // Restore bundle items (would need to re-fetch product data)
        // This is simplified - in production you'd want to fetch product data
        // from the DOM or make API calls to get full product objects

      } catch (error) {
        console.error('Error restoring bundle state:', error);
        localStorage.removeItem(this.storageKey);
      }
    }

    // Clear bundle state and reset UI
    clearBundleState() {
      try {
        // Clear localStorage
        localStorage.removeItem(this.storageKey);
        
        // Clear bundle items array
        this.bundleItems = [];
        
        // Reset slot count
        this.currentSlots = this.minItems;
        
        // Remove all items from slots
        const slots = this.querySelectorAll('[data-bundle-slot]');
        slots.forEach(slot => {
          const itemEl = slot.querySelector('.bundle-builder__item');
          if (itemEl) {
            itemEl.remove();
          }
          
          // Show empty state
          const emptyState = slot.querySelector('.bundle-builder__item-empty');
          if (emptyState) {
            emptyState.style.display = '';
          }
        });
        
        // Reset all product buttons to "Add" state
        this.updateAllProductButtons();
        
        // Update pricing to show $0
        this.updatePricing();
        
        // Reset subscription toggle if exists
        if(this.setOneTimePurchaseAfterAdd) {
          if (this.subscriptionToggle) {
            this.subscriptionToggle.checked = false;
            this.updateSubscriptionUI();
            this.updateMilestoneLabels();
            this.updatePricing();
          }
        }
        
        // Remove extra slots (keep only minItems)
        const allSlots = Array.from(slots);
        if (allSlots.length > this.minItems) {
          allSlots.slice(this.minItems).forEach(slot => slot.remove());
        }
      } catch (error) {
        console.error('Error clearing bundle state:', error);
      }
    }
  });
}